import React, { useState, useEffect } from 'react';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import CustomFormTitle from './CustomFormTitle';
import styled from 'styled-components';
import { FormDatePickerInterface } from '../../Models/Common/FormFieldComponentInterfaces';
import { IconCalendar } from '@tabler/icons-react';
import { IconButton } from '@mui/material';

import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import DateHelper from "../../Utilities/Helpers/DateHelper";

import { DateViewTypeEnum } from '../../Utilities/Enums/DateViewTypeEnum';

const StyledPickerContainer = styled.div<{ $isdarkmode: boolean; $disabled: boolean; }>`
    // width: -webkit-fill-available;

    .MuiStack-root {
        .MuiFormControl-root.MuiPickersTextField-root {
            // width: -webkit-fill-available;
        }
    }

    .MuiInputBase-root {
        font-size: 0.9rem !important;
        color: ${props => props.theme.primaryText};
        width: 100%;
        height: 38px;

        &::placeholder {
            color: var(--fieldFocusedBorderPrimaryColor);
            opacity: 0.8;
        }

        .Mui-disabled {
            // background-color: ${(props) => (props.theme.disabled)};
            background-color: ${(props) => (props.$isdarkmode ? '#242728' : '#e9ebec')};
            -webkit-text-fill-color: var(--disabledFieldFontColor) !important;
        }
    }

    .MuiPickersSectionList-root {
        padding: 7px !important;
    }

    .MuiOutlinedInput-root {
        padding-right: 5px !important;
    }

    .MuiTextField-root {
        margin-top: 0px !important;
        // background: ${props => props.theme.fieldBackgroundColor};
        background-color: ${(props) => (!props.$disabled ? props.theme.fieldBackgroundColor : (props.$isdarkmode ? '#242728' : '#e9ebec'))};
    }

    .MuiInputBase-input {
        padding:  7px 8px !important; //text padding
    }

    .MuiFormHelperText-root {
        margin-left: 0px !important; //helper margin
    }

    .MuiIconButton-root {
        margin-right: 0px !important;
    }

    .MuiStack-root {
        padding-top: 0px !important; //distance to title
    }
`;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Kuala_Lumpur");

const FormDatePicker = (fieldProps: FormDatePickerInterface) => {
    let isDarkMode = LocalStorageHelper.isDarkMode();

    //States
    const [open, setOpen] = useState(false);
    const [pickerKey, setPickerKey] = React.useState(0); //this will be use to force to rerender the daterange picker to fix

    useEffect(() => {
        setPickerKey(prevKey => prevKey + 1);
    }, [fieldProps.fieldValue]);

    //Rendering UIs
    const renderDatePickerUI = () => {
        const errorMsg = (fieldProps.errorMessages ? fieldProps.errorMessages[fieldProps.name] : null) ?? null;
        let viewType: any = (fieldProps?.viewType == DateViewTypeEnum.YEAR ?
            ['year'] : (fieldProps?.viewType == DateViewTypeEnum.YEAR_MONTH ?
                ['year', 'month'] : ['year', 'month', 'day']
            )
        );
        
        return <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={['DatePicker', 'DatePicker']}>
                <DatePicker name={fieldProps.name}
                    key={pickerKey}
                    enableAccessibleFieldDOMStructure={false} //do not remove; fix for placeholder issue
                    views={viewType ?? ['year', 'month', 'day']}
                    defaultValue={fieldProps.fieldValue ? dayjs(new Date(fieldProps.fieldValue)) : null}
                    disabled={fieldProps.disabled}
                    disablePast={fieldProps.disablePast}
                    disableFuture={fieldProps.disableFuture}
                    format={(fieldProps.outputDateFormat != null && fieldProps.outputDateFormat != "" ? fieldProps.outputDateFormat : "DD/MM/YYYY")}
                    onChange={(newValue: any) => {
                        if (fieldProps.handleOnChange) {
                            // fieldProps.handleOnChange(newValue ? dayjs(newValue) : null);
                            fieldProps.handleOnChange(newValue ? DateHelper.getFormattedDate(newValue, "yyyy-MM-dd") : null);
                        }
                    }}
                    open={open}
                    onClose={() => { setOpen(false); }}
                    slotProps={{
                        textField: {
                            error: errorMsg != null && errorMsg != "",
                            helperText: errorMsg,
                            placeholder: fieldProps.placeholder ?? '',
                            InputProps: {
                                ...{
                                    endAdornment: <IconButton
                                        disabled={fieldProps.disabled}
                                        onClick={() => setOpen((prev) => !prev)}>
                                        <IconCalendar size={18} />
                                    </IconButton>
                                }
                            }
                        }
                    }}
                />
            </DemoContainer>
        </LocalizationProvider>
    }

    return <StyledPickerContainer $isdarkmode={isDarkMode} $disabled={fieldProps?.disabled ?? false} className="customDatePicker">
        {/* ---------TITLE---------- */}
        {!fieldProps.hideLabel && <CustomFormTitle title={fieldProps.title}
            required={fieldProps.required}
            hideLabel={fieldProps.hideLabel}
            exclamationHoverItemDisplayText={fieldProps.exclamationHoverItemDisplayText} />}

        {/* ---------DATE PICKER---------- */}
        {renderDatePickerUI()}
    </StyledPickerContainer>
}

export default FormDatePicker;